import './DailyRembleVideo.scss'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import parse from 'html-react-parser'
import Hls from 'hls.js'
import mux from 'mux-embed'
import { RWebShare } from 'react-web-share'

import { app } from '../../../config'
import {
  EVENT_ACTION_ENDED,
  EVENT_ACTION_PAGEVIEW,
  EVENT_CATEGORY_DAILY_REMBLE,
  saveUserEvent
} from 'components/common/api/UserEvents'
import { updateWatchedDailyRembleVideos } from 'components/common/api/DailyRemble'
import { ROUTE_DAILY_REMBLE } from 'components/common/helpers/Paths'
import { isInApp } from '../../common/helpers/Mobile'
import Loading from 'templates/Loading'
import Bookmark from 'components/bookmark/Bookmark'
import { KEY_PRACTICE_DAILY_REMBLE } from 'components/common/api'

// Test video element to test stream playability
const testVideo = document.createElement('video')

const _getVideoStreamUrl = data => {
  let videoStream = data.acf_extended.video_stream

  // Do we have a mux stream?
  if (data.acf_extended.mux_stream_id)
    videoStream = `https://stream.mux.com/${data.acf_extended.mux_stream_id}.m3u8`

  return videoStream
}

/**
 * Daily Remble Video component. This component is used to display the daily remble video.
 * `data` - Object containing the daily remble video data
 * `target` - Element to attach the video to
 * @param data - Object containing the daily remble video data
 * @param target - Element to attach the video to
 * @param autoplay - Boolean indicating whether the video should autoplay
 * @param hlsContainer - Element to attach the HLS video to
 */
function Video(data, target, autoplay = false, hlsContainer = null) {
  // Define the video and its properties
  // Create a video element for each video in JS, so that we can control it on iOS
  // with the `play` and `pause` methods
  // See https://arrangeactassert.com/posts/how-to-fix-the-request-is-not-allowed-by-the-user-agent-or-the-platform-in-the-current-context-possibly-because-the-user-denied-permission/
  const videoId = data.id || data.ID
  const vid = document.createElement('video')
  vid.width = '100%'
  vid.controls = ''
  vid.muted = true
  vid.loop = true
  vid.autoplay = autoplay
  vid.preload = autoplay ? 'auto' : 'none'
  vid.className = 'daily-remble-video'
  vid.id = `daily-remble-video-${videoId}`
  vid.playsInline = true
  vid.crossOrigin = 'anonymous'
  vid['type'] = 'video/mp4'

  // Set the poster image to an empty image, so that we don't wait for the poster
  vid.poster = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='

  const videoStream = _getVideoStreamUrl(data)

  // Do we have a stream and does the browser support HLS natively?
  if (videoStream && vid.canPlayType('application/vnd.apple.mpegurl')) {
    vid.src = videoStream
  } else if (videoStream && hlsContainer) {
    // HLS.js is supported on platforms that have Media Source Extensions (MSE) (e.g. Chrome, Safari)
    // For more Hls.js options, see https://github.com/dailymotion/hls.js
    hlsContainer.attachMedia(vid)
    hlsContainer.abrEwmaDefaultEstimate = 234310 // Set the ABR algorithm to use

    // Once the HLS.js instance is ready, we can set the source
    hlsContainer.on(Hls.Events.MEDIA_ATTACHED, () => {
      if (autoplay) hlsContainer.loadSource(videoStream)
    })

    // Automatically start the stream if autoplay is enabled
    hlsContainer.on(Hls.Events.MANIFEST_PARSED, (event, eventData) => {
      if (autoplay)
        vid.play().catch(err => console.warn('Autoplay rejected by browser'))
    })

    // Update our watched videos list in local storage when the stream ends
    hlsContainer.on(Hls.Events.BUFFER_EOS, (event, eventData) => {
      // Return early if we're not playing the stream
      if (!data || !vid.currentTime) return

      // Log the event
      saveUserEvent(EVENT_ACTION_ENDED, EVENT_CATEGORY_DAILY_REMBLE, videoId)

      // Update our list of watched videos
      updateWatchedDailyRembleVideos(videoId)
    })
  } else {
    // HLS.js is not supported on platforms that do not have Media Source
    // Extensions (MSE) enabled.
    // See https://github.com/video-dev/hls.js/ for more info
    //
    // When the browser has built-in HLS support (check using `canPlayType`),
    // we can provide an HLS manifest (i.e. .m3u8 URL) directly to the video
    // element through the `src` property. This is using the built-in support
    // of the plain video element, without using HLS.js.
    //
    // Note: it would be more normal to wait on the 'canplay' event below however
    // on Safari (where you are most likely to find built-in HLS support) the
    // video.src URL must be on the user-driven white-list before a 'canplay'
    // event will be emitted; the last video event that can be reliably
    // listened-for when the URL is not on the white-list is 'loadedmetadata'.

    // Default the source of the video to the MP4 version
    vid.src = data.acf_extended.video
  }

  if (target) target.appendChild(vid)

  return vid
}

/**
 * Daily Remble Video player component. This component is used to display the daily remble video.
 * @param {Object} params Iniitial props:
 * `video` - Daily Remble Video
 * `index` - Index of the Daily Remble Video within the player
 * `activeIndex` - The currently active Daily Remble Video
 * `shouldMute` - Whether the video should be muted
 * `setShouldMute` - Function to set the should mute state
 * For reference see: https://medium.com/yemeksepeti-teknoloji/what-ive-learned-from-working-with-html5-video-over-a-month-485c5d5c2045
 */
export default function DailyRembleVideo({
  video,
  shouldMute = false,
  shouldShowSubtitles = false,
  showBookmark = true,
  setShouldMute = () => {},
  setShouldShowSubtitles = () => {},
  setShowAuthorBio = () => {},
  setAuthorBio = () => {}
}) {
  const history = useHistory()

  // Globals
  const [mute, setMute] = useState(shouldMute)
  const [showSubtitles, setShowSubtitles] = useState(shouldShowSubtitles)

  // Refs
  const videoRef = useRef()
  const overlayRef = useRef()
  const timeoutRef = useRef()
  const hlsRef = useRef()

  // States
  const [videoElement, setVideoElement] = useState()
  const [pause, setPause] = useState(false)
  const [canPlay, setCanPlay] = useState(false)

  // Metadata
  const videoId = video?.id || video?.ID
  const videoTitle = video?.title?.rendered || video?.post_title || ''
  const authorName = video?.acf_extended?.author[0]?.post_title || ''
  const authorBio = video?.acf_extended?.author[0]?.post_content || ''

  /**
   * Event handler for when the user clicks to toggle subtitles
   */
  const handleSubtitlesClick = e => {
    // Stop the event from bubbling up to the video element
    e.stopPropagation()

    // Toggle the subtitles
    setShouldShowSubtitles(!showSubtitles)
  }

  /**
   * Event handler for when the user clicks to mute the video
   */
  const handleMuteClick = e => {
    // Stop the event from bubbling up to the video element
    e.stopPropagation()
    setShouldMute(!shouldMute)
  }

  /**
   * Event handler for when the user clicks to pause/play the video
   */
  const handleVideoClick = e => {
    showControls(true)
  }

  /**
   * Event handler for when the user clicks the author of the video
   * @param {Event} e Event object
   */
  const handleAuthorClick = e => {
    // Stop the event from bubbling up to the card
    e.stopPropagation()

    // Send the author bio data to the parent component
    setAuthorBio({
      name: authorName,
      bio: authorBio
    })
    setShowAuthorBio(true)
  }

  /**
   * Shows/hides the controls for the video
   * @param {Boolean} show Whether to show the controls
   */
  const showControls = useCallback((show = true) => {
    // Clear the timeout if it exists
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    // Hide video controls
    if (!show && overlayRef.current)
      return overlayRef.current.classList.add('d-none')

    // Remove the d-none class from the overlay
    if (overlayRef.current) overlayRef.current.classList.remove('d-none')

    // Automatically hide the controls after 5 seconds
    timeoutRef.current = setTimeout(() => {
      showControls(false)
    }, 5000)
  }, [])

  /**
   * Tries to load the HLS stream, if we have one
   */
  const tryLoadStream = useCallback(() => {
    let videoStream = _getVideoStreamUrl(video)

    // If we have a HLS.js instance, load the stream
    if (hlsRef.current && !hlsRef.current.url)
      hlsRef.current.loadSource(videoStream)
  }, [video])

  /**
   * Video initialization callback. This callback is called when the video is initialized.
   */
  const initializeVideo = useCallback(
    _ => {
      // Get the stream and initialize the video
      const initTime = Date.now()
      const videoStream = _getVideoStreamUrl(video)

      // Should we load the stream through HLS?
      const shouldLoadHls =
        videoStream &&
        !testVideo.canPlayType('application/vnd.apple.mpegurl') &&
        Hls.isSupported()

      let hlsContainer = shouldLoadHls ? new Hls() : null
      hlsRef.current = hlsContainer

      // Create the video element
      const videoEl = new Video(video, videoRef.current, true, hlsContainer)

      // Do we have subtitles?
      const subtitles = video.acf_extended.captions || ''

      // Once the video metadata is loaded, set the video element
      videoEl.addEventListener('loadedmetadata', () => {
        // Dynamically add the captions text track
        // See https://iandevlin.com/blog/2015/02/javascript/dynamically-adding-text-tracks-to-html5-video/
        const track = document.createElement('track')
        track.kind = 'subtitles'
        track.label = 'Subtitles'
        track.srclang = 'en'
        track.src = subtitles
        track.default = true

        // Add the track to the video element
        track.addEventListener('load', function () {
          const mode = shouldShowSubtitles && subtitles ? 'showing' : 'hidden'
          track.mode = mode
          videoEl.textTracks[0].mode = mode
        })

        videoEl.appendChild(track)
      })

      // Check for suspended video (i.e., ios is in low power mode)
      videoEl.addEventListener('suspend', event => {
        // Video streams will 'suspend' instead of 'pause' after playing through once
        // console.log('suspended', event)
      })

      // Playing event
      videoEl.addEventListener('playing', event => {
        // change active video id on url
        history.replace(`${ROUTE_DAILY_REMBLE}/${videoId}`)

        // Update our list of watched videos
        updateWatchedDailyRembleVideos(videoId)
      })

      // Once the video is ended, save the user event
      videoEl.addEventListener('ended', () => {
        try {
          // Replay the video
          setPause(false)
        } catch (e) {}
        // Swallow the error
      })

      // Add a listener for the videos timeupdate event
      videoEl.addEventListener('timeupdate', () => {
        // If the video is paused, save the user event
        // if (videoEl.paused) saveUserEvent(EVENT_CATEGORY_VIDEO, EVENT_ACTION_PAUSE, videoId)
      })

      // Add a listener for when the video is ready to play
      videoEl.addEventListener('canplay', () => {
        // Update our has started state
        setCanPlay(true)

        // Show the controls
        showControls(true)
      })

      // Add Mux monitor
      // See https://docs.mux.com/guides/data/monitor-hls-js for more info
      mux.monitor(videoEl, {
        debug: false,
        // pass in the 'hls' instance and the 'Hls' constructor
        hlsjs: hlsContainer,
        Hls,
        respectDoNotTrack: false, // Disable tracking of browsers where Do Not Track is enabled
        data: {
          env_key: app.mux.api_key, // required
          // Player Metadata
          player_name: 'Daily Remble Player', // any arbitrary string you want to use to identify this player
          player_init_time: initTime,
          // Video Metadata
          video_id: videoId, // ex: 'abcd123'
          video_title: parse(videoTitle),
          video_series: 'Daily Remble'
        }
      })

      // Set the video element and track
      setVideoElement(videoEl)
      setMute(shouldMute)
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [video, shouldMute, shouldShowSubtitles]
  )

  /**
   * Mute effect.
   */
  useEffect(() => {
    // Mute video, if necessary
    if (videoElement) videoElement.muted = shouldMute
    setMute(shouldMute)
  }, [shouldMute, videoElement])

  /**
   * Subtitles effect.
   */
  useEffect(() => {
    // Show subtitles, if necessary
    try {
      if (videoElement) {
        videoElement.textTracks[0].mode = shouldShowSubtitles
          ? 'showing'
          : 'hidden'
      }
    } catch (e) {} // Silence the error
    setShowSubtitles(shouldShowSubtitles)
  }, [shouldShowSubtitles, videoElement])

  /**
   * Pause effect.
   */
  useEffect(() => {
    try {
      // Return early if we don't have a reference to the video
      if (!videoElement) return

      if (pause) {
        videoElement.pause()
      } else if (videoRef.current) {
        // Do we have an HLS stream? Let's try to load it if we haven't yet
        tryLoadStream()

        // Catch failed play events due to user agent issues
        videoElement.play().catch(() => {
          setPause(true)
        })
      }
    } catch (e) {} // Swallow exceptions
  }, [pause, videoElement, tryLoadStream])

  /**
   * Main effect for the component.
   */
  useEffect(() => {
    // Return early if we don't have video data or if the video element is already set
    if (!videoId || videoElement) return

    initializeVideo()
    tryLoadStream()

    // Log access to the video
    saveUserEvent(EVENT_CATEGORY_DAILY_REMBLE, EVENT_ACTION_PAGEVIEW, videoId)
  }, [videoId, videoElement, initializeVideo, tryLoadStream, showControls])

  return (
    <div className='daily-remble-video-wrapper'>
      <div
        className='daily-remble-video-overlay px-4 px-sm-5 pb-4 pt-5 d-flex flex-column justify-content-between align-items-center'
        ref={overlayRef}
        onClick={() => canPlay && showControls(false)}
      >
        <Loading isLoaded={!canPlay}>
          <div className='daily-remble-video-details pt-3 d-flex justify-content-between align-items-center'>
            <div className='align-self-start text-left'>
              <div className='daily-remble-video-title'>
                {parse(videoTitle)}
              </div>
              <span
                className='daily-remble-video-author'
                onClick={handleAuthorClick}
              >
                {authorName}
              </span>
            </div>

            <div className='d-flex flex-column gap-3'>
              {showBookmark && (
                <Bookmark
                  category={KEY_PRACTICE_DAILY_REMBLE}
                  categoryObjectId={videoId}
                />
              )}

              {/* Share button starts */}
              <RWebShare
                data={{
                  text: parse(videoTitle),
                  url: window.location.href
                }}
                disableNative={!isInApp()}
              >
                <button className='bg-transparent border-0 text-white fs-3'>
                  <i className='bi bi-share-fill' />
                </button>
              </RWebShare>
              {/* Share button ends */}
            </div>
          </div>

          <div className='daily-remble-video-controls w-100 d-flex justify-content-between align-items-center'>
            <button className='bg-transparent border-0 text-white'>
              <i
                className='bi bi-arrow-counterclockwise'
                onClick={e => {
                  e.stopPropagation()
                  videoElement.currentTime = 0
                }}
              />
            </button>
            <button className='bg-transparent border-0 text-white'>
              <i
                className={`bi bi-${pause ? 'play-fill' : 'pause'}`}
                onClick={e => {
                  e.stopPropagation()
                  setPause(!pause)
                }}
              />
            </button>
            <button className='bg-transparent border-0 text-white'>
              <i
                className={`bi bi-volume-${mute ? 'mute' : 'up'}`}
                onClick={handleMuteClick}
              />
            </button>
            <button className='bg-transparent border-0 text-white'>
              <i
                className={`bi bi-badge-${showSubtitles ? 'cc-fill' : 'cc'}`}
                onClick={handleSubtitlesClick}
              />
            </button>
          </div>
        </Loading>
      </div>
      <div
        className='daily-remble-video-player'
        ref={videoRef}
        onClick={handleVideoClick}
      />

      {pause && (
        <div
          className='daily-remble-video-pause'
          onClickCapture={() => setPause(false)}
        >
          <i className='icon bi-play-fill'></i>
        </div>
      )}
    </div>
  )
}
